/**
 * AiSummaryWidget — executive AI safety summary. On-demand: nothing is fetched
 * on dashboard load; the user clicks "Generate" to call /dashboard/executive
 * (which runs the legacy summary engine). The section is collapsible, and the
 * summary can be regenerated. No summary logic is reimplemented here.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import {
  Sparkles, AlertTriangle, ChevronDown, ChevronRight, RefreshCw, Loader2,
} from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isError = (s) => typeof s === 'string' && /unavailable|failed|not configured|error/i.test(s);

export default function AiSummaryWidget() {
  const { siteId } = useDashboard();
  const [collapsed, setCollapsed] = useState(false);
  const [requested, setRequested] = useState(false); // gate: fetch only on demand
  const [generatedFor, setGeneratedFor] = useState(undefined); // site the summary reflects

  // Read the site from a ref so the loader uses the CURRENT site when triggered,
  // but changing site does NOT retrigger the fetch (deps intentionally empty) —
  // the summary only (re)generates on an explicit user action.
  const siteIdRef = useRef(siteId);
  siteIdRef.current = siteId;

  // enabled=false until the user asks, so the dashboard never auto-generates.
  // Note: no refreshMs — an AI summary is generated on request, not polled.
  const { data, loading, error, refetch } = useDashboardData(
    (signal) => {
      const sid = siteIdRef.current;
      const p = sid ? `?site_id=${encodeURIComponent(sid)}` : '';
      return axios.get(`${API}/dashboard/executive${p}`, { signal }).then((r) => r.data);
    },
    [],
    { enabled: requested },
  );

  const summary = data?.ai_summary;
  const warn = isError(summary);
  // Site changed since the shown summary was generated → prompt manual refresh.
  const stale = requested && !loading && (data || error) && generatedFor !== siteId;
  const generate = () => {
    setGeneratedFor(siteId); // record the scope this run reflects
    if (requested) refetch(); else setRequested(true);
  };

  return (
    <div className={`rounded-xl border p-4 shadow-sm transition-shadow duration-200 hover:shadow-md ${warn ? 'border-amber-200 bg-amber-50/60' : 'border-blue-200/70 bg-blue-50/50'}`}>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className={`flex items-center gap-2 text-sm font-semibold ${warn ? 'text-amber-800' : 'text-blue-900'}`}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {warn ? <AlertTriangle className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
          AI Safety Summary
        </button>
        {!collapsed && (
          <button
            type="button"
            onClick={generate}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {data || error ? 'Regenerate' : 'Generate'}
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="mt-2">
          {!requested && !loading && (
            <p className="text-sm text-blue-900/70">
              Click <span className="font-medium">Generate</span> to create an AI summary of the current safety posture.
            </p>
          )}
          {loading && <div className="h-16 animate-pulse rounded bg-white/60" />}
          {!loading && error && (
            <div className="text-sm text-red-600">Failed to generate summary. Try again.</div>
          )}
          {!loading && !error && requested && (
            <p className={`text-sm leading-relaxed ${warn ? 'text-amber-700' : 'text-blue-900'}`}>
              {summary || 'No summary available.'}
            </p>
          )}
          {stale && (
            <p className="mt-2 text-xs italic text-gray-500">
              Site scope changed — click Regenerate to update this summary.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
